import { ConsultationType, SystemMessagesType } from '../../pages/types';
import { IntlShape } from 'react-intl';
import { DateTime } from 'luxon';
import { consultationTimeToFormatSeconds } from '../../pages/SchedulePage/utils';

type DataForMessage = {
  [SystemMessagesType.session_booked]: {
    consultation_time: ConsultationType['consultation_time'];
    display_name: string;
  };
  [SystemMessagesType.session_canceled]: {
    consultation_time: ConsultationType['consultation_time'];
    display_name: string;
    reason: string;
  };
  [SystemMessagesType.subscription_paused]: {
    therapistName: string;
  };
  [SystemMessagesType.subscription_un_paused]: {
    therapistName: string;
    date: string;
  };
  [SystemMessagesType.subscription_create]: {
    sessionQuantity: string;
    monthInterval: string;
    therapistName: string;
  };
  [SystemMessagesType.session_rescheduled]: {
    userName: string;
    formattedOldConsultationTime: number;
    formattedNewConsultationTime: string;
    reason: string;
  };
  [SystemMessagesType.subscription_cancel]: {
    therapistName: string;
    formattedSubscriptionEndDate: string;
  };
};

export const formatSystemMessage = <T extends SystemMessagesType>(
  messageType: T,
  messageData: DataForMessage[T],
  intl: IntlShape
) => {
  switch (messageType) {
    case SystemMessagesType.session_booked:
      const bookSessionMessage = `<b>${intl.formatMessage(
        {
          id: 'booking.system_message.session_booked',
          defaultMessage: '{name} забронював(ла) сеанс на:',
        },
        {
          name: (messageData as DataForMessage[SystemMessagesType.session_booked]).display_name,
        }
      )}</b>`;
      const timeMessage = `<time>${DateTime.fromSeconds(
        consultationTimeToFormatSeconds(
          (messageData as DataForMessage[SystemMessagesType.session_booked]).consultation_time
        )
      ).toSeconds()}</time>`;

      return bookSessionMessage + '\n' + timeMessage;
    case SystemMessagesType.session_canceled:
      const cancelSessionMessage = `<b>${intl.formatMessage(
        {
          id: 'booking.system_message.session_canceled',
          defaultMessage: '{name} скасував(ла) сеанс:',
        },
        {
          name: (messageData as DataForMessage[SystemMessagesType.session_canceled]).display_name,
        }
      )}</b>`;
      const cancelSessionTime = `<time>${consultationTimeToFormatSeconds(
        (messageData as DataForMessage[SystemMessagesType.session_canceled]).consultation_time
      )}</time>`;
      const cancelSessionReason = `${intl.formatMessage({
        id: 'booking.system_message.reason',
        defaultMessage: '<b>Причина:</b>',
      })}\n${(messageData as DataForMessage[SystemMessagesType.session_canceled]).reason}`;

      return cancelSessionMessage + '\n' + cancelSessionTime + '\n\n' + cancelSessionReason;
    case SystemMessagesType.subscription_paused:
      const subscriptionPausedMessage = `<b>${intl.formatMessage(
        {
          id: 'subscription.system_message.pause_1',
          defaultMessage: `Підписку з {name}. призупинено.`,
        },
        {
          name: (messageData as DataForMessage[SystemMessagesType.subscription_paused]).therapistName,
        }
      )}</b>`;
      const reactivateSubscriptionMessage = `${intl.formatMessage({
        id: 'subscription.system_message.pause_2',
        defaultMessage: `Ви можете будь-коли активувати свій план.`,
      })}`;
      const reactivateSubscriptionMessage2 = `${intl.formatMessage({
        id: 'subscription.system_message.pause_3',
        defaultMessage: `Під час паузи Ви не зможете записуватися на сеанси.`,
      })}`;

      return (
        subscriptionPausedMessage + '\n\n' + reactivateSubscriptionMessage + '\n\n' + reactivateSubscriptionMessage2
      );
    case SystemMessagesType.subscription_un_paused:
      const subscriptionActiveMessage = `<b>${intl.formatMessage(
        {
          id: 'subscription.system_message.unpause_1',
          defaultMessage: 'Підписку з {name}. відновлено.',
        },
        {
          name: (messageData as DataForMessage[SystemMessagesType.subscription_un_paused]).therapistName,
        }
      )}</b>`;
      const nextPaymentDateMessage = `${intl.formatMessage(
        {
          id: 'subscription.system_message.unpause_2',
          defaultMessage: 'Ваша наступна платіжна дата {date}',
        },
        {
          date: (messageData as DataForMessage[SystemMessagesType.subscription_un_paused]).date,
        }
      )}`;
      const bookingMessage = `${intl.formatMessage(
        {
          id: 'subscription.system_message.unpause_3',
          defaultMessage: 'Ви знову можете записуватись на сеанси.',
        },
        {
          date: (messageData as DataForMessage[SystemMessagesType.subscription_un_paused]).date,
        }
      )}`;

      return subscriptionActiveMessage + '\n\n' + nextPaymentDateMessage + '\n\n' + bookingMessage;
    case SystemMessagesType.subscription_create:
      const subscriptionCreateMessage = `<b>${intl.formatMessage(
        {
          id: 'subscription.system_message.create',
          defaultMessage: 'Підписку з {name}. оформлено.',
        },
        {
          name: (messageData as DataForMessage[SystemMessagesType.subscription_create]).therapistName,
        }
      )}</b>`;
      const intervalMessage = intl.formatMessage(
        {
          id: 'subscription.system_message.create.interval',
          defaultMessage: '{sessionQuantity} протягом {monthInterval}.',
        },
        {
          sessionQuantity: (messageData as DataForMessage[SystemMessagesType.subscription_create]).sessionQuantity,
          monthInterval: (messageData as DataForMessage[SystemMessagesType.subscription_create]).monthInterval,
        }
      );
      const bookMessage = intl.formatMessage({
        id: 'subscription.system_message.create.book',
        defaultMessage: 'Записуйся на сеанси на зручний для Вас час.',
      });

      return subscriptionCreateMessage + '\n\n' + intervalMessage + '\n\n' + bookMessage;
    case SystemMessagesType.session_rescheduled:
      const sessionRescheduledMessage = intl.formatMessage(
        {
          id: 'booking.system_message.reschedule',
          defaultMessage: '{name} перенес(ла) сеанс:',
        },
        {
          name: (messageData as DataForMessage[SystemMessagesType.session_rescheduled]).userName,
        }
      );
      const oldTimeMessage = `<b>${intl.formatMessage({
        id: 'booking.system_message.reschedule.old_time',
        defaultMessage: 'Попередній час:',
      })}</b>\n<time>${
        (messageData as DataForMessage[SystemMessagesType.session_rescheduled]).formattedOldConsultationTime
      }</time>`;
      const newTimeMessage = `<b>${intl.formatMessage({
        id: 'booking.system_message.reschedule.new_time',
        defaultMessage: 'Новий час:',
      })}</b>\n<time>${
        (messageData as DataForMessage[SystemMessagesType.session_rescheduled]).formattedNewConsultationTime
      }</time>`;
      const reasonMessage = `${intl.formatMessage({
        id: 'booking.system_message.reason',
        defaultMessage: '<b>Причина:</b>',
      })}\n${(messageData as DataForMessage[SystemMessagesType.session_rescheduled]).reason}`;

      return sessionRescheduledMessage + '\n\n' + oldTimeMessage + '\n\n' + newTimeMessage + '\n\n' + reasonMessage;
    case SystemMessagesType.subscription_cancel:
      const subscriptionCancelMessage = `<b>${intl.formatMessage(
        {
          id: 'subscription.system_message.canceled_1',
          defaultMessage: 'Підписку з {therapistFullName} скасовано.',
        },
        {
          therapistFullName: (messageData as DataForMessage[SystemMessagesType.subscription_cancel]).therapistName,
        }
      )}</b>`;
      const subscriptionCancelContinueMessage = `${intl.formatMessage({
        id: 'subscription.system_message.canceled_2',
        defaultMessage: 'Ви ще можете записуватися на сеанси доки Ваш поточний план не закінчиться',
      })} <b>${
        (messageData as DataForMessage[SystemMessagesType.subscription_cancel]).formattedSubscriptionEndDate
      }</b>`;

      return subscriptionCancelMessage + '\n\n' + subscriptionCancelContinueMessage;
    default:
      return '';
  }
};
